import React from "react";

// Renders a finance calendar grid for one month.
// Expects `data` shape from /api/finance/calendar or open_calendar action.result.
export default function FinanceCalendarPanel({ data }) {
  if (!data) return null;
  const { label, first_weekday = 0, days_in_month = 30, cells = [], totals = {} } = data;

  // Index cells by day-of-month for O(1) lookup
  const byDay = {};
  for (const c of cells) {
    const day = parseInt((c.date || "").slice(8, 10), 10);
    if (day) byDay[day] = c;
  }

  // Build a 6-row grid (max), Mon-Sun
  const slots = [];
  for (let i = 0; i < first_weekday; i++) slots.push(null);
  for (let d = 1; d <= days_in_month; d++) slots.push(d);
  while (slots.length % 7 !== 0) slots.push(null);

  const weeks = [];
  for (let i = 0; i < slots.length; i += 7) weeks.push(slots.slice(i, i + 7));

  const fmt = (n) => {
    const abs = Math.abs(n);
    const sign = n >= 0 ? "+" : "−";
    if (abs >= 1000) return `${sign}£${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
    return `${sign}£${abs.toFixed(0)}`;
  };

  return (
    <div data-testid="finance-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl rc-glow-text tracking-widest">{(label || "").toUpperCase()}</div>
        <div className="flex gap-3 text-[10px]">
          <div className="rc-label">NET <span className={`ml-1 font-display text-sm ${totals.net >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(totals.net || 0).toFixed(2)}</span></div>
          <div className="rc-label">TRADES <span className={`ml-1 font-display text-sm ${(totals.trades_pnl || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(totals.trades_pnl || 0).toFixed(2)}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-[9px] rc-label mb-1 opacity-70">
        {["MON","TUE","WED","THU","FRI","SAT","SUN"].map(d => <div key={d} className="text-center">{d}</div>)}
      </div>

      <div className="space-y-1">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7 gap-1">
            {week.map((d, di) => {
              if (!d) return <div key={di} className="aspect-[1.1] border border-transparent" />;
              const cell = byDay[d];
              const net = cell?.net || 0;
              const has = !!cell;
              const positive = net > 0;
              const negative = net < 0;
              const borderClass = has
                ? (positive ? "border-[var(--rc-gold)]" : negative ? "border-red-500/60" : "border-[var(--rc-gold-dim)]")
                : "border-[var(--rc-gold-dim)]/30";
              const tipParts = has ? [
                `${cell.date}`,
                cell.income ? `Income £${cell.income.toFixed(2)}` : null,
                cell.outgoing ? `Outgoing £${cell.outgoing.toFixed(2)}` : null,
                cell.trades_pnl ? `Trades £${cell.trades_pnl.toFixed(2)} (${cell.trades_count})` : null,
                `Net £${cell.net.toFixed(2)}`,
              ].filter(Boolean).join("\n") : null;
              return (
                <div
                  key={di}
                  title={tipParts || ""}
                  data-testid={`calendar-day-${d}`}
                  className={`relative border ${borderClass} p-1.5 min-h-[58px] flex flex-col justify-between bg-black/40`}
                >
                  <div className="text-[10px] rc-label opacity-80">{d}</div>
                  {has && (
                    <div className={`font-display text-[11px] tabular-nums leading-tight ${positive ? "rc-glow-text" : negative ? "text-red-400" : "text-[var(--rc-text-dim)]"}`}>
                      {fmt(net)}
                    </div>
                  )}
                  {has && cell.trades_count > 0 && (
                    <div className="absolute top-1 right-1 text-[8px] rc-label opacity-60">{cell.trades_count}T</div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
        <div className="border border-[var(--rc-gold-dim)] p-2"><div className="rc-label text-[9px]">INCOME</div><div className="font-display text-sm rc-glow-text mt-0.5 tabular-nums">£{(totals.income || 0).toFixed(2)}</div></div>
        <div className="border border-[var(--rc-gold-dim)] p-2"><div className="rc-label text-[9px]">OUTGOING</div><div className="font-display text-sm text-red-400 mt-0.5 tabular-nums">£{(totals.outgoing || 0).toFixed(2)}</div></div>
        <div className="border border-[var(--rc-gold-dim)] p-2"><div className="rc-label text-[9px]">TRADES P&amp;L</div><div className={`font-display text-sm mt-0.5 tabular-nums ${(totals.trades_pnl || 0) >= 0 ? "rc-glow-text" : "text-red-400"}`}>£{(totals.trades_pnl || 0).toFixed(2)}</div></div>
      </div>
    </div>
  );
}
